/**
 * Helper functions to work with lesson items
 * Provides a unified way to extract and work with items across all courses
 */

import { getSentenceAudioPattern, getVocabAudioPattern, getLessonNumberPadding, shouldInjectTitle, getCourseBehavior } from '../services/courseService';

/**
 * Convert vocab text to audio filename
 * @param {string} text - The vocab word/phrase
 * @returns {string} - Encoded filename (without extension)
 */
export function textToVocabFilename(text) {
  return text
    .toLowerCase()
    .replace(/ /g, '_')
    .replace(/[.!?]+$/, ''); // Strip trailing punctuation
}

/**
 * Flatten items in a lesson to a single list
 * @param {Object} lesson - The lesson configuration
 * @param {Object} options - Options for what to include
 * @param {boolean} options.includeLetters - Include letter items (default: true)
 * @param {boolean} options.includeVocab - Include vocab items (default: true)
 * @param {boolean} options.includeSentences - Include sentence/lyric items (default: true)
 * @param {Object} courseContext - Course context for generating audio URLs
 * @param {string} courseContext.courseId - The course ID (e.g., 'nce1')
 * @param {string} courseContext.lessonNumber - The lesson number (e.g., '001')
 * @returns {Array} - Flattened array of items with type and page metadata
 */
export function buildItemsList(lesson, options = {}, courseContext = {}) {
  const {
    includeLetters = true,
    includeVocab = true,
    includeSentences = true,
  } = options;

  const { courseId, lessonNumber } = courseContext;

  if (!lesson?.pages) return [];

  const items = [];

  // Get audio patterns for this course (null if course doesn't use pattern-based audio)
  const sentenceAudioPattern = courseId ? getSentenceAudioPattern(courseId) : null;
  const vocabAudioPattern = courseId ? getVocabAudioPattern(courseId) : null;

  // Inject lesson title as first recordable sentence item (courses with injectLessonTitle)
  if (includeSentences && lesson?.title && shouldInjectTitle(courseId)) {
    let titleText = lesson.title;
    let titleTranslation;

    if (getCourseBehavior(courseId, 'bilingualTitle', false)) {
      // Split "中文 English Title" into text + translation
      const titleParts = lesson.title.match(/^([\u4e00-\u9fff\u3000-\u303f]+)\s+(.+)$/);
      titleText = titleParts ? titleParts[2] : lesson.title;
      titleTranslation = titleParts ? titleParts[1] : undefined;
    }

    const titleItem = {
      id: 'title',
      text: titleText,
      translation: titleTranslation,
      type: 'sentence',
      page: 1,
      repeats: 3,
    };

    // Generate audio URL from pattern (sentenceIndex 0 = title)
    if (sentenceAudioPattern && lessonNumber) {
      const padding = getLessonNumberPadding(courseId);
      const formattedLesson = padding > 0
        ? String(parseInt(lessonNumber, 10)).padStart(padding, '0')
        : String(parseInt(lessonNumber, 10));
      titleItem.audio = sentenceAudioPattern
        .replace(/{book}/g, lesson.book || '')
        .replace(/{lessonNumber}/g, formattedLesson)
        .replace(/{sentenceIndex}/g, '0');
    }

    items.push(titleItem);
  }

  // Track sentence index across pages for audio URL generation
  let sentenceIndex = 0;

  Object.entries(lesson.pages).forEach(([pageNum, page]) => {
    const { sections } = page;
    if (!sections) return;

    // Add letters (phonics only)
    if (includeLetters && sections.letters) {
      items.push(
        ...sections.letters.map(item => ({
          ...item,
          type: 'letter',
          page: Number(pageNum),
          repeats: item.repeats ?? 3,
        }))
      );
    }

    // Add vocab
    if (includeVocab && sections.vocab) {
      items.push(
        ...sections.vocab.map(item => {
          const vocabItem = {
            ...item,
            type: 'vocab',
            page: Number(pageNum),
            repeats: item.repeats ?? 3,
          };

          // Generate audio URL from pattern if no explicit audio
          if (!vocabItem.audio && vocabAudioPattern) {
            vocabItem.audio = vocabAudioPattern
              .replace(/{word}/g, textToVocabFilename(item.text));
          }

          return vocabItem;
        })
      );
    }

    // Add sentences/lyrics
    if (includeSentences && sections.sentences) {
      items.push(
        ...sections.sentences.map(item => {
          sentenceIndex++;
          const sentenceItem = {
            ...item,
            type: 'sentence', // unified type for sentences/lyrics
            page: Number(pageNum),
            repeats: item.repeats ?? 3,
          };

          // Generate audio URL from pattern if no explicit audio
          if (!sentenceItem.audio && sentenceAudioPattern && lessonNumber) {
            const padding = getLessonNumberPadding(courseId);
            const formattedLesson = padding > 0
              ? String(parseInt(lessonNumber, 10)).padStart(padding, '0')
              : String(parseInt(lessonNumber, 10));

            sentenceItem.audio = sentenceAudioPattern
              .replace(/{book}/g, lesson.book || '')
              .replace(/{lessonNumber}/g, formattedLesson)
              .replace(/{sentenceIndex}/g, String(sentenceIndex));
          }

          return sentenceItem;
        })
      );
    }
  });

  return items;
}

/**
 * Get only vocab items from a lesson
 * @param {Object} lesson - The lesson configuration
 * @param {boolean} includeLetters - Include letter items for phonics (default: true)
 * @returns {Array} - Array of vocab items
 */
export function getVocabItems(lesson, includeLetters = true) {
  return buildItemsList(lesson, {
    includeLetters,
    includeVocab: true,
    includeSentences: false,
  });
}

/**
 * Get only sentence/lyric items from a lesson
 * @param {Object} lesson - The lesson configuration
 * @param {Object} courseContext - Course context for generating audio URLs
 * @returns {Array} - Array of sentence items
 */
export function getSentenceItems(lesson, courseContext = {}) {
  return buildItemsList(lesson, {
    includeLetters: false,
    includeVocab: false,
    includeSentences: true,
  }, courseContext);
}

/**
 * Get items for a specific page
 * @param {Object} lesson - The lesson configuration
 * @param {number} pageNumber - The page number (1 or 2)
 * @returns {Array} - Array of items from that page
 */
export function getPageItems(lesson, pageNumber) {
  const allItems = buildItemsList(lesson);
  return allItems.filter(item => item.page === pageNumber);
}

/**
 * Count total items in a lesson
 * @param {Object} lesson - The lesson configuration
 * @returns {Object} - Counts by type {letters, vocab, sentences, total}
 */
export function countItems(lesson) {
  const allItems = buildItemsList(lesson);

  const counts = {
    letters: allItems.filter(i => i.type === 'letter').length,
    vocab: allItems.filter(i => i.type === 'vocab').length,
    sentences: allItems.filter(i => i.type === 'sentence').length,
  };

  counts.total = counts.letters + counts.vocab + counts.sentences;

  return counts;
}

/**
 * Check if a lesson has vocab items
 * @param {Object} lesson - The lesson configuration
 * @returns {boolean}
 */
export function hasVocabItems(lesson) {
  if (!lesson?.pages) return false;
  return Object.values(lesson.pages).some(page =>
    page.sections?.vocab?.length > 0
  );
}

/**
 * Check if a lesson has sentence/lyric items
 * @param {Object} lesson - The lesson configuration
 * @returns {boolean}
 */
export function hasSentenceItems(lesson) {
  if (!lesson?.pages) return false;
  return Object.values(lesson.pages).some(page =>
    page.sections?.sentences?.length > 0
  );
}

/**
 * Build a single "combined" sentence item from multiple sentences.
 * Used for 整段背诵 (paragraph recitation) mode.
 * @param {Array} sentences - Array of sentence items (with text, translation, audio, videoStart, videoEnd)
 * @param {string|null} combinedAudio - URL for full passage audio (optional)
 * @param {string|null} lessonTitle - Title to prepend (optional)
 * @returns {Object|null} - Combined item or null if no sentences
 */
/**
 * Build a single mega-item from all vocab items for "combined vocab" recording.
 * Student records ALL vocab words in 1 take. Used in LEARN pipeline Stage 1 Tab 2.
 * @param {Array} vocabItems - Array of vocab items (with text, translation)
 * @returns {Object|null} - Combined vocab item or null if no items
 */
export function buildCombinedVocabItem(vocabItems) {
  if (!vocabItems || vocabItems.length === 0) return null;
  const text = vocabItems.map((v, i) => `${i + 1}. ${v.text}`).join('\n');
  const translation = vocabItems
    .filter(v => v.translation)
    .map((v, i) => `${i + 1}. ${v.translation}`)
    .join('\n');
  return {
    id: 'combined-vocab',
    text,
    translation: translation || null,
    type: 'vocab',
    audio: null,
    repeats: 1,
  };
}

export function buildCombinedSentenceItem(sentences, combinedAudio = null, lessonTitle = null) {
  if (!sentences || sentences.length === 0) return null;

  // Filter out the injected title item — it has no video timestamps or audio
  const realSentences = sentences.filter(s => s.id !== 'title');
  if (realSentences.length === 0) return null;

  const numberedText = realSentences.map((s, i) => `${i + 1}. ${s.text}`).join('\n');
  const combinedText = lessonTitle ? `${lessonTitle}\n\n${numberedText}` : numberedText;
  const combinedTranslation = realSentences
    .filter(s => s.translation)
    .map((s, i) => `${i + 1}. ${s.translation}`)
    .join('\n');

  return {
    id: 'combined',
    text: combinedText,
    translation: combinedTranslation || null,
    type: 'sentence',
    videoStart: realSentences[0].videoStart,
    videoEnd: realSentences[realSentences.length - 1].videoEnd,
    audio: combinedAudio || realSentences[0].audio,
    repeats: 1,
  };
}
